import { Link } from "react-router-dom";
import { MoveRight, RotateCw, Disc3, Vibrate } from "lucide-react";
import SEO from "../components/site/SEO";
import Reveal from "../components/site/Reveal";
import PricingTable from "../components/site/PricingTable";
import { useDevis } from "../components/site/DevisForm";
import { useConfig } from "../components/Layout";
import { IMAGES, TARIFS_DEFAUT } from "../lib/constants";

const SIGNES = [
  { icone: MoveRight, texte: "La voiture tire d'un côté sur une route droite, sans toucher au volant." },
  { icone: RotateCw, texte: "Le volant n'est pas droit quand vous roulez en ligne." },
  { icone: Disc3, texte: "Les pneus s'usent sur un seul bord, intérieur ou extérieur." },
  { icone: Vibrate, texte: "Des vibrations apparaissent dans la direction." },
];

export default function Parallelisme() {
  const config = useConfig();
  const tarif = config?.tarifs?.parallelisme || TARIFS_DEFAUT.parallelisme;
  const { openDevis } = useDevis();

  return (
    <>
      <SEO
        title="Parallélisme au Cannet — Réglage géométrie des roues | Promo Pneus"
        description="Réglage du parallélisme au Cannet, près de Cannes : train avant, 4 roues, contrôle géométrie. Voiture qui tire, pneus usés d'un côté ? Sans rendez-vous."
      />
      <section className="bg-brand-dark py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-heading font-semibold uppercase tracking-[0.3em] text-brand-red text-base mb-3">
              {"//"} Chapitre 04
            </p>
            <h1 className="font-heading font-bold uppercase text-5xl sm:text-6xl md:text-7xl text-white tracking-wide leading-none">
              Parallélisme
            </h1>
            <p className="mt-4 text-lg text-white/80 max-w-2xl">
              Réglage de la géométrie des trains roulants : train avant et 4 roues.
              Une voiture qui va droit, des pneus qui durent.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <PricingTable titre={tarif.titre} rows={tarif.rows} testid="tarifs-parallelisme" />
        </div>
      </section>

      <section className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 sm:space-y-24">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
              <div>
                <p className="font-heading font-bold text-6xl text-brand-light leading-none mb-2" aria-hidden="true">01</p>
                <h2 className="font-heading font-semibold uppercase text-3xl sm:text-4xl text-brand-dark tracking-tight mb-5">
                  Qu'est-ce que le parallélisme ?
                </h2>
                <p className="text-brand-body leading-relaxed text-base md:text-lg mb-4">
                  Le parallélisme, c'est l'alignement des roues entre elles et par rapport à l'axe
                  de votre véhicule. Avec le carrossage et la chasse, il définit la géométrie
                  complète des trains roulants.
                </p>
                <p className="text-brand-body leading-relaxed text-base md:text-lg">
                  Un réglage correct se mesure au dixième de degré. Un choc, une pièce d'usure
                  ou une réparation de direction suffisent à le dérégler — et c'est votre
                  portefeuille qui paie la différence.
                </p>
              </div>
              <div className="relative">
                <div className="absolute -top-3 -left-3 w-24 h-24 border-t-4 border-l-4 border-brand-red" aria-hidden="true" />
                <img src={IMAGES.parallelisme} alt="Capteurs de parallélisme fixés sur la roue et écran de mesure des angles"
                  className="w-full h-72 sm:h-96 object-cover relative" loading="lazy" />
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="bg-brand-light p-6 sm:p-10">
              <p className="font-heading font-bold text-6xl text-white leading-none mb-2" aria-hidden="true">02</p>
              <h2 className="font-heading font-semibold uppercase text-3xl sm:text-4xl text-brand-dark tracking-tight mb-6">
                Les signes qui doivent alerter
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {SIGNES.map((s, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white p-5">
                    <s.icone size={26} className="text-brand-red shrink-0 mt-0.5" aria-hidden="true" />
                    <p className="text-brand-dark font-body leading-relaxed">{s.texte}</p>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-brand-body text-lg">
                Un seul de ces signes suffit : passez à l'atelier, le contrôle est rapide et sans rendez-vous.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="bg-brand-light p-6 sm:p-10">
              <p className="font-heading font-bold text-6xl text-white leading-none mb-2" aria-hidden="true">03</p>
              <h2 className="font-heading font-semibold uppercase text-3xl sm:text-4xl text-brand-dark tracking-tight mb-5">
                Quand faire régler son parallélisme
              </h2>
              <p className="text-brand-body leading-relaxed text-base md:text-lg mb-4 max-w-3xl">
                Après un choc : un trottoir monté un peu vite, un nid-de-poule, un dos-d'âne
                pris trop sec. Le réglage ne pardonne pas. Même réflexe après toute intervention
                sur la suspension ou la direction : rotules, amortisseurs, crémaillère.
              </p>
              <p className="text-brand-body leading-relaxed text-base md:text-lg max-w-3xl">
                C'est aussi le bon moment à chaque{" "}
                <Link to="/pneumatique" data-testid="parallelisme-lien-pneus"
                  className="text-brand-red font-semibold underline hover:text-brand-redhover transition-colors">
                  montage de pneus neufs
                </Link>{" "}
                : les roues sont déjà démontées, le contrôle est facilité. Et en routine,
                comptez une vérification environ tous les deux ans.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="bg-brand-light p-6 sm:p-10">
              <p className="font-heading font-bold text-6xl text-white leading-none mb-2" aria-hidden="true">04</p>
              <h2 className="font-heading font-semibold uppercase text-3xl sm:text-4xl text-brand-dark tracking-tight mb-5">
                Les bénéfices
              </h2>
              <p className="text-brand-body leading-relaxed text-base md:text-lg mb-4 max-w-3xl">
                Des pneus qui vivent plus longtemps : une géométrie déréglée peut user un pneu
                neuf en quelques milliers de kilomètres. Le réglage coûte moins cher que le
                train de pneus qu'il protège.
              </p>
              <p className="text-brand-body leading-relaxed text-base md:text-lg max-w-3xl">
                Une consommation réduite, un freinage plus sûr, un virage plus stable et une
                meilleure tenue de route. Bref : une voiture qui va où vous la pointez,
                sans corriger en permanence.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-brand-light">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold uppercase text-3xl sm:text-4xl text-brand-dark tracking-tight mb-6">
              Pourquoi choisir Promo Pneus pour votre parallélisme ?
            </h2>
            <p className="text-brand-body text-lg leading-relaxed mb-4">
              Nous mesurons avant de régler : contrôle complet de la géométrie, puis réglage de ce
              qui est réglable. Si une pièce usée bloque le réglage — rotule, silentbloc, biellette —
              on vous le montre et on chiffre la réparation avant de toucher à quoi que ce soit.
            </p>
            <p className="text-brand-body text-lg leading-relaxed mb-8">
              Vous repartez avec un train aligné et un volant droit. Et si vous venez pour des pneus
              neufs, on en profite pour vérifier la géométrie : roues démontées, contrôle facilité.
            </p>
            <button data-testid="parallelisme-devis-bouton" onClick={() => openDevis("Demande concernant : parallélisme. ")}
              className="bg-brand-red hover:bg-brand-redhover text-white font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 transition-colors">
              Demander un devis parallélisme
            </button>
          </Reveal>
        </div>
      </section>
    </>
  );
}
